import React, { useEffect, useState } from "react";

import {
    Box,
    Paper,
    TextField,
    Grid,
    styled,
    Card,
    Divider,
    IconButton,
    Button,
    Typography,
    InputAdornment,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Checkbox
} from '@mui/material';

import dayjs from "dayjs";

import { useNavigate } from "react-router-dom";
import { useFormik } from 'formik';
import { useSystemPrompt } from "../../../../../hooks/useSystemPrompt"
import { useAppContextFeatures } from "../../../../../context/AppContext";

import DatePickerComponent from "../../../../../components/common/DatePickerComponent"
import CommonBreadcumbs from '../../../../../components/common/CommonBreadcumbs';
import { CommonIcons, icons } from "../../../../../components/common/CommonIcons";
import CommonSectionHeader from "../../../../../components/common/CommonSectionHeader";
import CommonRestricted from '../../../../../components/common/CommonRestricted';

import { useAuthAccess } from "../../../../../hooks/useAuthAccess";
import { useTranslations } from "../../../../../hooks/useTranslations";

import { useLocations, locationTypes } from "../../../../../hooks/api/useLocations";

import {
    containerInjectionProps,
    initialValues,
    validationSchema,
    initialRowValues,
    containerSizes,
    containerTypes,
    useContainer
} from "../../../hooks/model/useContainer"

import { useSystemRoutes, routeTypes } from "../../../../../hooks/useSystemRoutes"
import { useSystemHead, headTitles } from '../../../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from "../../../../../hooks/usePermissions";

import LocationSelector from "../../../../../components/selector/LocationSelector"

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export const StyledIcon = styled(Box)({
    width: 20,
    height: 20,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
});

export default function NewContainer({
    routeType
}) {
    const { t } = useTranslations({ ns: ["_@repositories", "_prompt", "common", "_common"] })

    const { promptMessage } = useSystemPrompt();
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.repository.id, scopeTypes.repository.container.id)

    const { addContainer } = useContainer({ routeType })

    const { getAvailableLocations } = useAuthAccess();
    const availableLocations = getAvailableLocations();

    const { list: listLocations } = useLocations({ locationType: locationTypes.location })

    const { setSystemLoading, getLocationIds, setLocationIds } = useAppContextFeatures();
    const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const [dataLocations, setDataLocations] = useState([])

    const onSubmit = async (currentValues) => {

        setSystemLoading(true)

        // console.log('currentValues', currentValues)

        try {

            let payload = {
                containerSize: currentValues?.containerSize,
                containerType: currentValues?.containerType,
                repository: {
                    repoNumber: currentValues?.repoNumber,
                    numberAlternative: currentValues?.repoAlternativeNumber,
                    repoDate: dayjs(currentValues?.initialDate).format("YYYY-MM-DD"),
                    remark: currentValues?.remark,
                },
                props: {
                    locationId: currentValues?.locationId,
                    toLocationId: currentValues?.toLocationId,
                    numberSeal: currentValues?.numberSeal,
                    numberMarking: currentValues?.repoMarking,
                    loadDate: dayjs(currentValues?.loadDate).format("YYYY-MM-DD"),
                    etaDate: '',
                    supervisor: currentValues?.supervisor,
                    shipper: currentValues?.shipper,
                    customAgent: currentValues?.customAgent
                },
            }

            let response = await addContainer(payload)

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("repositories/containers"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }

        setSystemLoading(false)
    }

    const fetchLocations = async () => {

        try {

            const props = {
                limit: -1,
                skip: 0,
                filterValue: [
                    // {
                    //     name: 'id',
                    //     operator: filterCustomer.anyOf,
                    //     value: JSON.stringify(productIds)
                    // }
                ],
                sortInfo: {
                    id: "id",
                    dir: 0
                },
                // dataTypes: []
            }

            // props.itemType = itemTypes.product

            const result = await listLocations(props)

            let allLocations = result?.data?.locations ? result.data.locations : []

            // skip the from location Id selection
            if (values?.locationId) {
                allLocations = allLocations.filter(i => i?.id !== values.locationId)
                setFieldValue(`toLocationId`, ``)
                setFieldValue(`toLocationName`, ``)
            }

            setDataLocations(allLocations)

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        return false

    }

    const validateLocationSelection = () => {
        const selected = getLocationIds()
        return selected && selected.length === 1
    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue, handleSubmit } = formik;

    // if only one warehouse available - auto select
    useEffect(() => {

        if (availableLocations.length === 1) {
            // console.log('availableLocations', availableLocations[0])
            setFieldValue(`locationId`, availableLocations[0]?.id)
            setFieldValue(`locationName`, availableLocations[0]?.location_name)
        }

    }, [])

    useEffect(() => {

        fetchLocations()

    }, [values.locationId])

    const RenderContainer = () => {

        return (
            <StyledCard sx={{ mt: 1 }} >
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={4} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t('container')}
                            subTitle={t('provideInformationAboutContainer')}
                        />

                    </Grid>

                    <Grid item xs={12} sm={12} md={8}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={12} md={6}>
                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    size="small"
                                    error={Boolean(errors?.containerSize)}
                                >
                                    <InputLabel id="containerSize">{t('containerSize')}</InputLabel>
                                    <Select
                                        label={t('containerSize')}
                                        size="small"
                                        name={`containerSize`}
                                        sx={{
                                            width: '100%',
                                            // mr: 2
                                        }}
                                        onChange={handleChange}
                                        value={values?.containerSize}
                                    >
                                        {
                                            Object.keys(containerSizes).map((i, index) => {
                                                return (
                                                    <MenuItem key={index} value={containerSizes[i].value}>
                                                        {containerSizes[i].display}
                                                    </MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                    <FormHelperText>
                                        {errors?.containerSize}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    size="small"
                                    error={Boolean(errors?.containerType)}
                                >
                                    <InputLabel id="containerType">{t('containerType')}</InputLabel>
                                    <Select
                                        label={t('containerType')}
                                        size="small"
                                        name={`containerType`}
                                        sx={{
                                            width: '100%',
                                            // mr: 2
                                        }}
                                        onChange={handleChange}
                                        value={values?.containerType}
                                    >
                                        {
                                            Object.keys(containerTypes).map((i, index) => {
                                                return (
                                                    <MenuItem key={index} value={containerTypes[i].value}>
                                                        {/* {containerTypes[i].display} */}
                                                        {t(containerTypes[i].id)}
                                                    </MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                    <FormHelperText>
                                        {errors?.containerType}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    autoFocus
                                    fullWidth
                                    name={"repoNumber"}
                                    label={t('repositoryNo')}
                                    type="text"
                                    variant="outlined"
                                    value={values.repoNumber}
                                    onChange={handleChange}
                                    error={touched.repoNumber && Boolean(errors.repoNumber)}
                                    helperText={touched.repoNumber && errors.repoNumber}
                                />
                            </Grid>
                            {/* <Grid item xs={6}>
                                <></>
                            </Grid> */}
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"repoAlternativeNumber"}
                                    label={t('repositoryAltNo')}
                                    type="test"
                                    variant="outlined"
                                    value={values.repoAlternativeNumber}
                                    onChange={handleChange}
                                    error={touched.repoAlternativeNumber && Boolean(errors.repoAlternativeNumber)}
                                    helperText={touched.repoAlternativeNumber && errors.repoAlternativeNumber}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"numberSeal"}
                                    label={t('sealNo')}
                                    type="text"
                                    variant="outlined"
                                    value={values.numberSeal}
                                    onChange={handleChange}
                                    error={touched.numberSeal && Boolean(errors.numberSeal)}
                                    helperText={touched.numberSeal && errors.numberSeal}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"repoMarking"}
                                    label={t('repositoryMarking')}
                                    type="text"
                                    variant="outlined"
                                    value={values.repoMarking}
                                    onChange={handleChange}
                                    error={touched.repoMarking && Boolean(errors.repoMarking)}
                                    helperText={touched.repoMarking && errors.repoMarking}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                                <DatePickerComponent
                                    config={{
                                        name: "initialDate",
                                        label: t('initialDate')
                                    }}
                                    formik={{
                                        values,
                                        errors,
                                        touched,
                                        handleChange,
                                        setFieldValue
                                    }}
                                    sx={{
                                        width: '100%'
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                                <DatePickerComponent
                                    config={{
                                        name: "loadDate",
                                        label: t('loadDate')
                                    }}
                                    formik={{
                                        values,
                                        errors,
                                        touched,
                                        handleChange,
                                        setFieldValue
                                    }}
                                    sx={{
                                        width: '100%'
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"supervisor"}
                                    label={t('supervisor')}
                                    type="text"
                                    variant="outlined"
                                    value={values.supervisor}
                                    onChange={handleChange}
                                    error={touched.supervisor && Boolean(errors.supervisor)}
                                    helperText={touched.supervisor && errors.supervisor}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"shipper"}
                                    label={t('shipper')}
                                    type="text"
                                    variant="outlined"
                                    value={values.shipper}
                                    onChange={handleChange}
                                    error={touched.shipper && Boolean(errors.shipper)}
                                    helperText={touched.shipper && errors.shipper}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"customAgent"}
                                    label={t('customAgent')}
                                    type="text"
                                    variant="outlined"
                                    value={values.customAgent}
                                    onChange={handleChange}
                                    error={touched.customAgent && Boolean(errors.customAgent)}
                                    helperText={touched.customAgent && errors.customAgent}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"remark"}
                                    label={t('remark')}
                                    type="text"
                                    variant="outlined"
                                    multiline
                                    rows={"3"}
                                    value={values.remark}
                                    onChange={handleChange}
                                    error={touched.remark && Boolean(errors.remark)}
                                    helperText={touched.remark && errors.remark}
                                />
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>

        )
    }

    const RenderFormLocation = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                        <CommonSectionHeader
                            title={t('location')}
                            subTitle={t('relatedWarehouseLocationToProcess')}
                        />
                    </Grid>

                    <Grid item xs={12} sm={12} md={8}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    disabled
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"locationId"}
                                    label={t('location')}
                                    type="text"
                                    variant="outlined"
                                    value={values.locationName}
                                    error={touched.locationId && Boolean(errors.locationId)}
                                    helperText={touched.locationId && errors.locationId}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    color="primary"
                                                    sx={{
                                                        my: 1
                                                    }}
                                                    onClick={() => {
                                                        setLocationIds([])
                                                    }}
                                                    disabled={
                                                        availableLocations.length === 1
                                                    }
                                                >
                                                    <StyledIcon
                                                        sx={{
                                                            width: 25,
                                                            height: 25,
                                                            m: 0,
                                                            p: 0
                                                        }}
                                                    >
                                                        <CommonIcons name={icons.edit} />
                                                    </StyledIcon>
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    size="small"
                                    error={Boolean(errors?.toLocationId)}
                                >
                                    <InputLabel id="toLocationId">{t('destinationLocation')}</InputLabel>
                                    <Select
                                        label={t('destinationLocation')}
                                        size="small"
                                        name={`toLocationId`}
                                        sx={{
                                            width: '100%',
                                            // mr: 2
                                        }}
                                        onChange={(event) => {

                                            let locId = event.target.value;

                                            // let locationSelected = availableLocations.filter(i => i.id === locId)

                                            setFieldValue(
                                                `toLocationId`,
                                                locId
                                            )
                                        }}
                                        value={values?.toLocationId ? values?.toLocationId : ""}
                                    >
                                        {
                                            dataLocations?.map((i, index) => {
                                                return (
                                                    <MenuItem key={index} value={i.id}>
                                                        {i.location_name}
                                                    </MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                    <FormHelperText>
                                        {errors?.toLocationId}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderForm = () => {

        return (
            <form onSubmit={handleSubmit}>

                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={12}>

                        {
                            RenderFormLocation()
                        }

                    </Grid>
                </Grid>

                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        {
                            RenderContainer()
                        }

                    </Grid>
                </Grid>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                        >
                            {t('saveChanges')}
                        </Button>
                    </Grid>
                </Grid>

            </form>
        )
    }

    const RenderAll = () => {

        return validateLocationSelection() ?
            (
                RenderForm()
            )
            :
            (
                <LocationSelector
                    config={{
                        title: t('selectOnly1Location'),
                        description: t('youHaveToSelectAnyoneOfTheWarehouseLocationToProceed'),
                    }}
                    maxSelect={1}
                    handleOnNext={({ selected }) => {

                        // console.log('selected', selected)

                        setLocationIds([selected[0].id])
                        setFieldValue(`locationId`, selected[0].id)
                        setFieldValue(`locationName`, selected[0].location_name)

                    }}
                />
            )

    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.newContainer })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('newContainer')}
                breadcrumbs={[
                    {
                        displayText: t('containers'),
                        props: {
                            href: getRoute(`repositories/containers`)
                        }
                    },
                    {
                        displayText: `${t('newContainer')} (*)`,
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {/* {JSON.stringify(errors)}
            {JSON.stringify(values)} */}

            {
                isPermissionAllow({ functionType: functionTypes.pageNew.id }) ?
                    RenderAll()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}